import '@fontsource-variable/inter-tight';
import 'ol/ol.css';
import {CssBaseline} from '@mui/material';
import {LocalizationProvider} from '@mui/x-date-pickers-pro';
import {AdapterLuxon} from '@mui/x-date-pickers-pro/AdapterLuxon';
import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {asyncWithLDProvider} from 'launchdarkly-react-client-sdk';
import {Settings} from 'luxon';
import {NuqsAdapter} from 'nuqs/adapters/react-router/v7';
import {useGeographic as setGeographic} from 'ol/proj';
import React from 'react';
import ReactDOM from 'react-dom/client';
import {Provider as ReduxProvider} from 'react-redux';
import {BrowserRouter} from 'react-router';
import SecureApp from './SecureApp.jsx';
import initSentry from './sentry.js';
import store from './store.ts';
import {checkWebGLSupport} from './utils/webgl.ts';
import {LD_CLIENTSIDE_ID} from '@src/config';
import appGlobalStyles from '@src/styles/AppGlobalStyles.jsx';
import ThemeProvider from '@src/styles/ThemeProvider.jsx';

// Default to CRS84 coordinate system.
setGeographic();

// Default to en-US and UTC
Settings.defaultLocale = 'en-US';
Settings.defaultZone = 'utc';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        if (
          error.response?.status === 429 ||
          (error.response?.status >= 500 && error.response?.status <= 599)
        ) {
          return failureCount < 3;
        }
        return false;
      },
    },
  },
});

// Polyfill for Windows
if (window !== undefined) {
  window.global = window;
}

// Ensure that all the blob support exists
if (Blob) {
  if (!Blob.prototype.text) {
    const reader = (blob, format) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = evt => {
          resolve(evt.target.result);
        };
        reader.onerror = () => {
          reject('Bad Blob');
        };
        if (format === 'text') {
          reader.readAsText(blob);
        } else {
          reader.readAsArrayBuffer(blob);
        }
      });
    };

    Blob.prototype.text = function () {
      return reader(this, 'text');
    };
    Blob.prototype.arrayBuffer = function () {
      return reader(this, 'arraybuffer');
    };
  }
}

// Setup Sentry
initSentry();

checkWebGLSupport();

const root = ReactDOM.createRoot(document.getElementById('root'));

(async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: LD_CLIENTSIDE_ID,
    options: {
      application: {
        id: 'acorns',
        version: import.meta.env.GIT_REV,
      },
    },
  });

  root.render(
    <NuqsAdapter>
      <ReduxProvider store={store}>
        <LDProvider>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter basename={import.meta.env.BASE_URL}>
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <ThemeProvider>
                  <CssBaseline />
                  {appGlobalStyles}
                  <SecureApp />
                </ThemeProvider>
              </LocalizationProvider>
            </BrowserRouter>
            <ReactQueryDevtools buttonPosition="bottom-left" />
          </QueryClientProvider>
        </LDProvider>
      </ReduxProvider>
    </NuqsAdapter>,
  );
})();
